import { createSignal, onCleanup } from 'solid-js'

/*
  code search's key map, as far as it makes sense here. the ones it has that we don't
  (outline, references, related files) are missing because the features are.

  single letters, no modifiers, exactly like the original - which is only tolerable
  because they're ignored while you're typing in a field
*/
export interface TShortcut {
  keys: string
  description: string
  group: 'global' | 'search' | 'file'
}

export const SHORTCUTS: TShortcut[] = [
  { keys: '?', description: 'show this help', group: 'global' },
  { keys: '/', description: 'focus the search box', group: 'global' },
  { keys: 'esc', description: 'leave the box / close what is open', group: 'global' },
  { keys: 'j / k', description: 'next / previous result', group: 'search' },
  { keys: 'enter', description: 'open the selected result', group: 'search' },
  { keys: '.', description: 'search inside the current directory', group: 'search' },
  { keys: 'c', description: 'focus the code panel', group: 'file' },
  { keys: 'f', description: 'find in file', group: 'file' },
  { keys: 'g', description: 'go to line', group: 'file' },
  { keys: 'n / N', description: 'next / previous match in file', group: 'file' },
  { keys: 'y', description: 'show/hide search matches', group: 'file' },
  { keys: 'j / k', description: 'down / up one line in the code browser', group: 'file' },
  { keys: '8', description: 'toggle the 80-column guide', group: 'file' },
  { keys: 'b', description: 'toggle the blame layer', group: 'file' },
  { keys: 't', description: 'toggle the directory tree', group: 'file' },
  { keys: 'r', description: 'switch branch or tag', group: 'file' },
  { keys: 'l then p', description: 'copy the file path', group: 'file' },
  { keys: 'l then r', description: 'copy a link to this line', group: 'file' },
  { keys: '+ / -', description: 'code font size', group: 'file' },
]

type THandler = (event: KeyboardEvent) => void

const handlers = new Map<string, THandler>()

/** true while the user is typing somewhere a letter key means a letter */
export function inTextField(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  return target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)
}

/*
  `l then p` and friends: the first key arms a prefix, the next one completes it.
  a second of grace is what code search feels like, and it clears itself so a stray
  `l` can't leave the keyboard in a strange mode
*/
const [pending, setPending] = createSignal<string | null>(null)
let pendingTimer: ReturnType<typeof setTimeout> | undefined

export { pending as pendingPrefix }

const PREFIXES = new Set(['l'])

function keyOf(event: KeyboardEvent): string | null {
  if (event.ctrlKey || event.metaKey || event.altKey) return null
  return event.key
}

window.addEventListener('keydown', (event) => {
  const key = keyOf(event)
  if (key === null) return

  // escape has to work from inside the search box - it's how you get out of it
  if (inTextField(event.target) && key !== 'Escape') return

  const prefix = pending()
  if (prefix) {
    clearTimeout(pendingTimer)
    setPending(null)
    const handler = handlers.get(`${prefix} ${key}`)
    if (handler) {
      event.preventDefault()
      handler(event)
      return
    }
  }

  if (PREFIXES.has(key) && [...handlers.keys()].some(it => it.startsWith(`${key} `))) {
    event.preventDefault()
    setPending(key)
    clearTimeout(pendingTimer)
    pendingTimer = setTimeout(setPending, 1500, null)
    return
  }

  const handler = handlers.get(key)
  if (!handler) return
  event.preventDefault()
  handler(event)
})

/**
 * binds keys for as long as the calling component is alive. later components win:
 * the file view's `f` should beat anything global, and it's the one mounted last
 */
export function useShortcuts(map: Record<string, THandler>) {
  const previous = new Map<string, THandler | undefined>()

  for (const [key, handler] of Object.entries(map)) {
    previous.set(key, handlers.get(key))
    handlers.set(key, handler)
  }

  onCleanup(() => {
    for (const [key, handler] of previous) {
      if (handler) handlers.set(key, handler)
      else handlers.delete(key)
    }
  })
}
